import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useMap from "@/lib/map/useMap";
import { getCropCycleV2Display } from "@/lib/utils/cropCycles";
import { getEnrollmentTierLabel } from "@/lib/utils/enrollment";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";
import { Organization } from "@/types/organization";
import { faChevronLeft, faGear } from "@fortawesome/free-solid-svg-icons";
import { faHubspot } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Badge,
    Button,
    Chip,
    Divider,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    Select,
    SelectItem,
    Skeleton,
    Tab,
    Tabs,
    useDisclosure,
} from "@heroui/react";
import {
    createFileRoute,
    Link,
    Outlet,
    redirect,
    useMatch,
    useNavigate,
    useParams,
    useSearch,
} from "@tanstack/react-router";
import { LngLatBoundsLike } from "mapbox-gl";
import { useEffect, useMemo } from "react";
import * as turf from "@turf/turf";
import useRequest from "@/lib/request/useRequest";
import classNames from "classnames";
import {
    FIELD_BOUNDARY_SOURCE,
    FIELD_FILL_LAYER,
    FIELD_LINE_LAYER,
    IRRIGATION_FILL_LAYER,
    IRRIGATION_LINE_LAYER,
    IRRIGATION_SOURCE,
    MZ_COLORS,
    PREFERRED_MANAGEMENT_ZONE_LINE_LAYER,
    PREFERRED_MANAGEMENT_ZONE_FILL_LAYER,
    PREFERRED_MANAGEMENT_ZONE_SOURCE,
    SECTOR_FILL_LAYER,
    SECTOR_LINE_LAYER,
    SECTOR_SOURCE,
    SECTOR_TEXT_LAYER,
} from "@/lib/map/layers";
import FieldSettings from "@/components/field/setup/FieldSettings";
import RecommendationCard from "@/components/field/RecommendationCard";
import { useTranslation } from "react-i18next";
import FieldNitrogenAdvisor from "@/components/field/FieldNitrogenAdvisor";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import useIsMobile from "@/lib/utils/useIsMobile";

const searchSchema = z.object({
    advisor: z.boolean().optional(),
});

export const Route = createFileRoute("/dashboard/fields/$fieldId")({
    component: RouteComponent,
    beforeLoad: ({ params, location }) => {
        if (location.pathname === `/dashboard/fields/${params.fieldId}`) {
            throw redirect({
                to: `/dashboard/fields/$fieldId/insights/ntime`,
                params,
                search: {
                    advisor: undefined,
                },
            });
        }
    },
    validateSearch: zodValidator(searchSchema),
});

function RouteComponent() {
    const { useGetRequest } = useRequest();
    const { fieldId } = useParams({
        from: "/dashboard/fields/$fieldId",
    });
    const { t } = useTranslation();
    const { cameFrom } = useSearch({
        strict: false,
    }) as { cameFrom?: string };
    const backRoute = useMemo(() => {
        return cameFrom ? `/dashboard/${cameFrom}` : "/dashboard/fields";
    }, [cameFrom]);
    const {
        cropCycleQueryV2,
        setSelectedCropCycle,
        selectedCropCycle,
        geometryQuery,
        fieldQuery,
    } = useFieldDashboard() ?? {};
    const cropCycles = useMemo(() => {
        return cropCycleQueryV2?.data ?? [];
    }, [cropCycleQueryV2?.data]);

    const hasDuplicateCropCycles = useMemo(() => {
        return cropCycles.some((cropCycle) => {
            return (
                cropCycles.filter((c) => c.start_date === cropCycle.start_date)
                    .length > 1
            );
        });
    }, [cropCycles]);

    const { map, mapLoaded } = useMap();

    const organizationQuery = useGetRequest<Organization>({
        url: `organizations/${fieldQuery?.data?.grower}/`,
        enabled: !!fieldQuery?.data,
    });

    const insights = useMatch({
        from: "/dashboard/fields/$fieldId/insights",
        shouldThrow: false,
    });
    const rx = useMatch({
        from: "/dashboard/fields/$fieldId/rx",
        shouldThrow: false,
    });
    const operations = useMatch({
        from: "/dashboard/fields/$fieldId/operations",
        shouldThrow: false,
    });
    const setup = useMatch({
        from: "/dashboard/fields/$fieldId/setup",
        shouldThrow: false,
    });

    const { isMobile } = useIsMobile();

    useEffect(() => {
        if (!map || !mapLoaded) return;
        if (geometryQuery?.data) {
            if (geometryQuery.data.field.geometry) {
                addUpdateGeojsonSource(map, FIELD_BOUNDARY_SOURCE, {
                    type: "geojson",
                    data: geometryQuery.data.field,
                    tolerance: 0,
                });

                addUpdateGeojsonLayers(map, [
                    FIELD_LINE_LAYER,
                    FIELD_FILL_LAYER,
                ]);
                map.fitBounds(
                    turf.bbox(
                        geometryQuery.data.field.geometry,
                    ) as LngLatBoundsLike,
                    {
                        padding: isMobile
                            ? {
                                  bottom: 10,
                                  left: 10,
                                  right: 10,
                                  top: 10,
                              }
                            : {
                                  bottom: 150,
                                  left: 50,
                                  right: 50,
                                  top: 50,
                              },
                        duration: 0,
                    },
                );
            }

            if (geometryQuery.data.irrigation_systems?.features?.length > 0) {
                addUpdateGeojsonSource(map, IRRIGATION_SOURCE, {
                    type: "geojson",
                    data: geometryQuery.data.irrigation_systems,
                    tolerance: 0,
                });
                addUpdateGeojsonLayers(map, [
                    IRRIGATION_FILL_LAYER,
                    IRRIGATION_LINE_LAYER,
                ]);
            }
            if (geometryQuery.data.treatment_sections?.features?.length > 0) {
                addUpdateGeojsonSource(map, SECTOR_SOURCE, {
                    type: "geojson",
                    data: geometryQuery.data.treatment_sections,
                    tolerance: 0,
                });
                addUpdateGeojsonLayers(map, [
                    SECTOR_FILL_LAYER,
                    SECTOR_LINE_LAYER,
                    SECTOR_TEXT_LAYER,
                ]);
            }
            if (geometryQuery.data.management_zones?.features?.length > 0) {
                const zones = geometryQuery.data.management_zones.features.map(
                    (zone, index) => ({
                        ...zone,
                        properties: {
                            ...zone.properties,
                            "fill-color": MZ_COLORS[index % MZ_COLORS.length],
                        },
                    }),
                );
                addUpdateGeojsonSource(map, PREFERRED_MANAGEMENT_ZONE_SOURCE, {
                    type: "geojson",
                    data: {
                        type: "FeatureCollection",
                        features: zones,
                    },
                    tolerance: 0,
                });
                addUpdateGeojsonLayers(map, [
                    PREFERRED_MANAGEMENT_ZONE_FILL_LAYER,
                    PREFERRED_MANAGEMENT_ZONE_LINE_LAYER,
                ]);
            }
        } else {
            removeLayersIfExists(map, [
                FIELD_LINE_LAYER.id,
                FIELD_FILL_LAYER.id,
            ]);
            removeSourcesIfExists(map, [FIELD_BOUNDARY_SOURCE]);
            removeLayersIfExists(map, [
                IRRIGATION_FILL_LAYER.id,
                IRRIGATION_LINE_LAYER.id,
            ]);
            removeSourcesIfExists(map, [IRRIGATION_SOURCE]);
            removeLayersIfExists(map, [
                SECTOR_FILL_LAYER.id,
                SECTOR_LINE_LAYER.id,
                SECTOR_TEXT_LAYER.id,
            ]);
            removeSourcesIfExists(map, [SECTOR_SOURCE]);
            removeLayersIfExists(map, [
                PREFERRED_MANAGEMENT_ZONE_FILL_LAYER.id,
                PREFERRED_MANAGEMENT_ZONE_LINE_LAYER.id,
            ]);
            removeSourcesIfExists(map, [PREFERRED_MANAGEMENT_ZONE_SOURCE]);
        }
    }, [geometryQuery?.data, isMobile, map, mapLoaded]);

    const tab = [insights, rx, operations, setup]
        ?.find((x) => !!x)
        ?.fullPath?.split("/")
        .pop();

    const { isOpen, onOpen, onClose } = useDisclosure();

    const { advisor } = Route.useSearch();

    const navigate = useNavigate();

    if (!fieldQuery?.data) {
        return (
            <div className="flex flex-col gap-1 p-2">
                <Skeleton className="w-full h-[20rem] rounded-lg" />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full overflow-y-scroll scrollbar-hide md:overflow-hidden md:scrollbar-default">
            {isOpen && (
                <Modal
                    isOpen={isOpen}
                    onClose={onClose}
                    isDismissable
                    size="3xl"
                    placement="top"
                    scrollBehavior="inside"
                >
                    <ModalContent>
                        <ModalHeader>
                            <div className="flex flex-row gap-2 justify-between w-full items-center pr-8">
                                <div>Management</div>
                                <div className="flex flex-row gap-2 items-center">
                                    {fieldQuery.data?.crm_id && (
                                        <FontAwesomeIcon
                                            icon={faHubspot}
                                            color="#f57722"
                                        />
                                    )}
                                </div>
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <FieldSettings />
                        </ModalBody>
                    </ModalContent>
                </Modal>
            )}
            <div className="flex flex-row gap-1 items-center p-2 w-full">
                <Button
                    variant="light"
                    isIconOnly
                    size="lg"
                    as={Link}
                    href={backRoute}
                    aria-label="Back to Fields"
                >
                    <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                </Button>

                <div className=" flex flex-col w-full">
                    <div className="flex flex-row justify-between w-full">
                        <p className="font-bold text-3xl">
                            {fieldQuery.data?.name}
                        </p>
                        <Button
                            isIconOnly
                            variant="flat"
                            aria-label="Manage"
                            onPress={onOpen}
                        >
                            <FontAwesomeIcon icon={faGear} size="xl" />
                        </Button>
                    </div>
                    <div className="flex flex-row gap-4 w-full">
                        <p>{organizationQuery.data?.name}</p>
                        <Chip
                            classNames={{
                                base: classNames({
                                    "bg-brandNavy-500 text-white":
                                        !!fieldQuery?.data?.active_enrollment,
                                    "bg-gray-600 text-white":
                                        !fieldQuery?.data?.active_enrollment,
                                }),
                            }}
                            size="sm"
                            radius="sm"
                        >
                            {getEnrollmentTierLabel(
                                fieldQuery?.data?.active_enrollment
                                    ?.enrollment_tier,
                            )}
                        </Chip>
                    </div>
                </div>
            </div>
            <div className="px-2 pb-2">
                <Badge
                    color="warning"
                    content="Duplicates!"
                    isInvisible={!hasDuplicateCropCycles}
                    classNames={{ base: "w-full" }}
                >
                    <Select
                        size="sm"
                        label={t("cropCycle", { ns: "insights" })}
                        labelPlacement="outside-left"
                        selectedKeys={
                            selectedCropCycle?.start_date && cropCycles.length
                                ? [selectedCropCycle.start_date]
                                : []
                        }
                        onChange={(e) => {
                            const selected = cropCycles?.find(
                                (c) => c.start_date === e.target.value,
                            );
                            if (selected) {
                                setSelectedCropCycle?.(selected);
                            }
                        }}
                    >
                        {(cropCycles ?? []).map((cropCycle) => (
                            <SelectItem key={cropCycle.start_date}>
                                {getCropCycleV2Display(cropCycle)}
                            </SelectItem>
                        ))}
                    </Select>
                </Badge>
            </div>
            <RecommendationCard />
            <Tabs
                radius="none"
                fullWidth
                selectedKey={tab}
                color="primary"
                variant="light"
                classNames={{
                    tabList: "p-0 pt-1",
                }}
            >
                <Tab
                    title="Insights"
                    key="insights"
                    as={Link}
                    // @ts-expect-error ts(2322 )
                    to="/dashboard/fields/$fieldId/insights/ntime"
                    params={{
                        fieldId,
                    }}
                    // @ts-expect-error ts(2322 )
                    search={(prev) => prev}
                />
                <Tab
                    title="Rx"
                    key="rx"
                    as={Link}
                    // @ts-expect-error ts(2322 )
                    to="/dashboard/fields/$fieldId/rx"
                    params={{
                        fieldId,
                    }}
                    // @ts-expect-error ts(2322 )
                    search={(prev) => prev}
                />
                <Tab
                    title="Operations"
                    key="operations"
                    as={Link}
                    // @ts-expect-error ts(2322 )
                    to="/dashboard/fields/$fieldId/operations"
                    params={{
                        fieldId,
                    }}
                    // @ts-expect-error ts(2322 )
                    search={(prev) => prev}
                />
                <Tab
                    title="Setup"
                    key="setup"
                    as={Link}
                    // @ts-expect-error ts(2322 )
                    to="/dashboard/fields/$fieldId/setup"
                    params={{
                        fieldId,
                    }}
                    // @ts-expect-error ts(2322 )
                    search={(prev) => prev}
                />
            </Tabs>
            <Divider />
            <div className="md:overflow-y-scroll md:scrollbar-default h-full">
                <Outlet />
            </div>
            {advisor && (
                <div className="z-50 absolute bottom-0 right-0 md:h-screen h-[calc(100vh-56px)] w-screen md:w-[800px] bg-white p-2 pl-4 rounded-l-md shadow-md overflow-y-scroll">
                    <FieldNitrogenAdvisor
                        fieldId={fieldId}
                        onClose={() =>
                            navigate({
                                to: ".",
                                search: (prev) => ({
                                    ...prev,
                                    advisor: undefined,
                                }),
                            })
                        }
                    />
                </div>
            )}
        </div>
    );
}
